// MagmaNewsSection.jsx — RR Site news on Magma
// Fetches from WP REST, renders Row3 (top 3 with thumbnails) + NERMoreNews list (next 6)
// Self-fetching, no props required. Drop into MarketOverviewPage replacing NewsCardsSection.

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const WP_API      = '/api/wp/wp-json/rr/v1/noticias';
const MARKET_API  = '/api/go/api/ticker';
const MAGMA_ASSET = '/api/magma/asset/stock';
const RR_SITE     = 'https://relatorioreservado.com.br';

// ── Helpers ──────────────────────────────────────────────────
function fixImageUrl(url) {
  if (!url) return null;
  return url.replace('/api/wp/wp-content', 'https://relatorioreservado.com.br/wp-content');
}

function decodeEntities(html) {
  if (!html) return '';
  if (typeof document !== 'undefined') {
    const t = document.createElement('textarea');
    t.innerHTML = html;
    return t.value;
  }
  return html.replace(/&amp;/g,'&').replace(/&lt;/g,'<').replace(/&gt;/g,'>').replace(/&quot;/g,'"').replace(/&#8220;/g,'\u201C').replace(/&#8221;/g,'\u201D').replace(/&#8211;/g,'\u2013').replace(/&#8217;/g,'\u2019').replace(/&nbsp;/g,' ');
}

function formatDate(str) {
  if (!str) return '';
  const d = new Date(str);
  const diff = Math.floor((Date.now() - d) / 3600000);
  if (diff < 1) return 'Agora';
  if (diff < 24) return `${diff}h atrás`;
  const days = Math.floor(diff / 24);
  if (days === 1) return 'Ontem';
  if (days < 7) return `${days} dias atrás`;
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
}

function articleUrl(id) {
  return `${RR_SITE}/noticia/${id}`;
}

// ── Ticker chip (self-fetching price) ────────────────────────
function TickerChip({ ticker }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetch(`${MARKET_API}/${ticker}`)
      .then(r => r.json())
      .then(setData)
      .catch(() => {});
  }, [ticker]);

  const pct   = data?.var ?? null;
  const price = data?.ult ?? null;
  const pos   = pct === null ? true : pct >= 0;

  return (
    <a
      href={`${MAGMA_ASSET}/${ticker.toUpperCase()}`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={e => e.stopPropagation()}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '3px 8px',
        background: 'rgba(255,255,255,0.06)',
        borderLeft: `2px solid ${pos ? '#4ade80' : '#f87171'}`,
        borderRadius: 3,
        fontFamily: "'IBM Plex Mono',monospace",
        fontSize: 10, fontWeight: 600, color: '#fff',
        textDecoration: 'none', whiteSpace: 'nowrap',
      }}
    >
      <span style={{ letterSpacing: '0.04em' }}>{ticker}</span>
      {price !== null && (
        <span style={{ color: 'rgba(255,255,255,0.55)', fontWeight: 500, fontSize: 9 }}>
          R${Number(price).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      )}
      {pct !== null && (
        <span style={{ color: pos ? '#4ade80' : '#f87171', fontWeight: 800, fontSize: 9 }}>
          {pos ? '▲' : '▼'}{pos ? '+' : ''}{pct.toFixed(2)}%
        </span>
      )}
    </a>
  );
}

// ── Category tag ─────────────────────────────────────────────
function Tag({ label }) {
  if (!label) return null;
  return (
    <span style={{
      fontFamily: "'IBM Plex Mono',monospace",
      fontSize: 8, fontWeight: 700, letterSpacing: '0.14em',
      color: '#6660fe', textTransform: 'uppercase',
      border: '1px solid #6660fe44', padding: '2px 6px', borderRadius: 2,
      display: 'inline-block', marginBottom: 4,
    }}>{label}</span>
  );
}

// ── Row3 — 3 cards, 16/9 thumbnail, title ────────────────────
function Row3({ items }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 20,
      paddingBottom: 28, borderBottom: '1px solid rgba(255,255,255,0.07)', marginBottom: 28,
    }}>
      {items.map((a) => (
        <a key={a.id} href={articleUrl(a.id)} target="_blank" rel="noopener noreferrer"
          style={{ textDecoration: 'none', display: 'block' }}
        >
          <div style={{
            aspectRatio: '16/9',
            background: a.thumbnail
              ? `url(${a.thumbnail}) center/cover`
              : 'linear-gradient(135deg,#1a1020,#2a1a2a)',
            borderRadius: 8, border: '1px solid rgba(255,255,255,0.07)',
            marginBottom: 10, overflow: 'hidden',
          }} />
          {a.categories?.[0] && <Tag label={a.categories[0]} />}
          <h3 style={{
            fontFamily: "'Roble',system-ui,sans-serif",
            fontSize: 15, fontWeight: 700, color: 'rgba(255,255,255,0.88)',
            lineHeight: 1.25, marginTop: 6, marginBottom: 6,
            letterSpacing: '-0.01em',
            display: '-webkit-box', WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>{a.title}</h3>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontWeight: 400 }}>
            {formatDate(a.date)}
          </div>
          {a.ner_ticker?.length > 0 && (
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
              {a.ner_ticker.slice(0, 3).map(t => <TickerChip key={t} ticker={t} />)}
            </div>
          )}
        </a>
      ))}
    </div>
  );
}

// ── Grid2 — 2-col grid for the second batch ──────────────────
function Grid2({ items }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 16,
    }}>
      {items.map((a) => (
        <a key={a.id} href={articleUrl(a.id)} target="_blank" rel="noopener noreferrer"
          style={{ textDecoration: 'none', display: 'flex', gap: 12, alignItems: 'flex-start' }}
          onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
          onMouseLeave={e => e.currentTarget.style.opacity = '1'}
        >
          <div style={{
            width: 80, height: 56, flexShrink: 0,
            background: a.thumbnail
              ? `url(${a.thumbnail}) center/cover`
              : 'linear-gradient(135deg,#1a1020,#2a1a2a)',
            borderRadius: 5, border: '1px solid rgba(255,255,255,0.07)',
          }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            {a.categories?.[0] && <Tag label={a.categories[0]} />}
            <h4 style={{
              fontFamily: "'Roble',system-ui,sans-serif",
              fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.82)',
              lineHeight: 1.25, marginTop: 3, marginBottom: 4,
              display: '-webkit-box', WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>{a.title}</h4>
            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.25)', fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontWeight: 400 }}>
              {formatDate(a.date)}
            </div>
          </div>
        </a>
      ))}
    </div>
  );
}

// ── Company filter ───────────────────────────────────────────
// Strip B3 share-class / number suffixes so "PETROBRAS PN" -> "petrobras"
function cleanCompanyName(name) {
  if (!name) return '';
  return name
    .replace(/\b(ON|PN|PNA|PNB|PNC|PND|UNT|N1|N2|NM|DRN)\b/gi, '')
    .replace(/\d+/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function filterByCompany(articles, ticker, companyName) {
  const tk      = (ticker || '').toUpperCase();
  const tkLower = tk.toLowerCase();
  const phrase  = cleanCompanyName(companyName);

  return articles.filter(a => {
    const tickerHit = (a.ner_ticker || []).some(t => String(t).toUpperCase() === tk);
    if (tickerHit) return true;
    const hay = `${a.title || ''} ${a.excerpt || ''}`.toLowerCase();
    if (tkLower.length >= 4 && hay.includes(tkLower)) return true;     // e.g. "PETR4" mentioned in text
    if (phrase.length >= 3 && hay.includes(phrase)) return true;       // full cleaned name as a phrase
    return false;
  });
}

// ── Main export ───────────────────────────────────────────────
// offset: pass 0, 9, 18... for each instance on the page so they show different articles
// ticker/companyName: when set, the feed is filtered to that company only
// label: section header text (default "Acervo Editorial")
export function MagmaNewsSection({ category = '', offset = 0, ticker = '', companyName = '', label = 'Acervo Editorial' }) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    let alive = true;
    const filterMode = !!(ticker || companyName);
    const load = async () => {
      try {
        // fetch a larger pool so offset slicing works across instances
        // (and so company filtering has a wide enough net)
        const perPage = filterMode ? 50 : 30;
        let url = `${WP_API}?per_page=${perPage}&_=${Date.now()}`;
        if (category) url += `&category=${encodeURIComponent(category)}`;
        const res  = await fetch(url);
        const data = await res.json();
        if (!alive) return;
        const mapped = (data.articles || []).map(a => ({
          ...a,
          title:     decodeEntities(a.title),
          excerpt:   decodeEntities(a.excerpt),
          thumbnail: fixImageUrl(a.thumbnail),
        }));
        setArticles(filterMode ? filterByCompany(mapped, ticker, companyName) : mapped);
        setLoading(false);
      } catch { setLoading(false); }
    };
    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => { alive = false; clearInterval(id); };
  }, [category, ticker, companyName]);

  if (loading) return (
    <div style={{ padding: '2rem 0', fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, color: 'rgba(255,255,255,0.2)', letterSpacing: '0.15em' }}>
      CARREGANDO NOTÍCIAS…
    </div>
  );

  // slice this instance's window: 3 for Row3 + 4 for Grid2 = 7 per instance
  const window = articles.slice(offset, offset + 7);
  if (!window.length) return null;

  const row3 = window.slice(0, 3);
  const grid = window.slice(3);       // up to 4 items in 2-col grid

  return (
    <div style={{ width: '100%' }}>
      {/* section label — only show on first instance */}
      {offset === 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '1.2rem' }}>
          <div style={{ width: 3, height: 22, background: 'linear-gradient(180deg,#6660fe,rgba(102,96,254,0))', borderRadius: 2 }} />
          <span style={{ fontFamily: "'Roble',system-ui,sans-serif", fontSize: '1.3rem', fontWeight: 800, color: '#fff', letterSpacing: '-0.02em' }}>
            {label}
          </span>
          <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 9, fontWeight: 700, color: '#6660fe', border: '1px solid #6660fe44', padding: '2px 7px', borderRadius: 2, letterSpacing: '0.12em' }}>
            RR
          </span>
          <a href={RR_SITE} target="_blank" rel="noopener noreferrer"
            style={{ fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", marginLeft: 'auto', color: 'rgba(255,255,255,0.3)', textDecoration: 'none', fontSize: '0.8rem', fontWeight: 400, borderBottom: '1px solid rgba(255,255,255,0.15)', paddingBottom: 1 }}>
            Ver acervo completo ↗
          </a>
        </div>
      )}

      <Row3 items={row3} />
      {grid.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <Grid2 items={grid} />
        </div>
      )}
    </div>
  );
}

export default MagmaNewsSection;